import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  BellRing, 
  FileSearch, 
  TrendingUp, 
  BarChart3, 
  AlertTriangle,
  User,
  ShieldAlert,
  ShieldCheck,
  Activity,
  FileText,
  Shield, Key, Users, LogOut, Clock, Lock, Settings,
  X, Hash, ExternalLink, Radio,
  Menu, ChevronLeft, ChevronRight, Trash2, Download, RefreshCw,
  Briefcase
} from 'lucide-react';
import { API_BASE_URL } from './config';
import apiService from './services/apiService';
import type { MonitoringScope } from './types';
import Landing from './Landing';
import { Login, Signup } from './Auth';
import NarrativeIntelligence from './pages/NarrativeIntelligence';
import CampaignSimulation from './pages/CampaignSimulation';
import OSINTMap from './components/OSINTMap';
import PropagationGraph from './components/PropagationGraph';
import SimulationSandbox from './components/SimulationSandbox';
import IntelligenceCenter from './pages/IntelligenceCenter';
import { GlobalMonitoringDashboard } from './pages/GlobalMonitoringDashboard';
import BrandProtection from './pages/BrandProtection';
import BusinessIntelligence from './pages/BusinessIntelligence';
import { AlertManagement } from './pages/AlertManagement';
import { PostInvestigation } from './pages/PostInvestigation';
import { TrendingTopics } from './pages/TrendingTopics';
import { Analytics } from './pages/Analytics';

import { useFeatureFlags } from './hooks/useFeatureFlags';
import { getAgencyLabel, getAgencyProfile } from './utils/agencyProfiles';

const IDLE_LOGOUT_MS = 2 * 60 * 60 * 1000; // 2 hours

const DEFAULT_MONITORING_SCOPE: MonitoringScope = {
  scope_type: 'GLOBAL',
  country_code: null,
  region_code: null,
};

const INDIA_REGION_HINTS: Array<{ label: string; code: string; patterns: RegExp[] }> = [
  { label: 'Karnataka', code: 'IN-KA', patterns: [/\bkarnataka\b/i, /\bbengaluru\b/i, /\bbangalore\b/i, /\bmysuru\b/i] },
  { label: 'Delhi', code: 'IN-DL', patterns: [/\bdelhi\b/i, /\bnew delhi\b/i] },
  { label: 'Maharashtra', code: 'IN-MH', patterns: [/\bmaharashtra\b/i, /\bmumbai\b/i, /\bpune\b/i] },
  { label: 'Tamil Nadu', code: 'IN-TN', patterns: [/\btamil nadu\b/i, /\bchennai\b/i, /\bcoimbatore\b/i] },
  { label: 'Telangana', code: 'IN-TS', patterns: [/\btelangana\b/i, /\bhyderabad\b/i] },
  { label: 'Andhra Pradesh', code: 'IN-AP', patterns: [/\bandhra pradesh\b/i, /\bvisakhapatnam\b/i, /\bvijayawada\b/i] },
  { label: 'Kerala', code: 'IN-KL', patterns: [/\bkerala\b/i, /\bkochi\b/i, /\bthiruvananthapuram\b/i] },
  { label: 'West Bengal', code: 'IN-WB', patterns: [/\bwest bengal\b/i, /\bkolkata\b/i, /\bcalcutta\b/i] },
  { label: 'Gujarat', code: 'IN-GJ', patterns: [/\bgujarat\b/i, /\bahmedabad\b/i, /\bsurat\b/i] },
  { label: 'Uttar Pradesh', code: 'IN-UP', patterns: [/\buttar pradesh\b/i, /\blucknow\b/i, /\bnoida\b/i] },
];

function deriveRegionalCodeFromLocation(locationInput?: string | null): { country_code: string | null; region_code: string | null } {
  const normalizedLocation = (locationInput || '').trim();
  if (!normalizedLocation) {
    return { country_code: null, region_code: null };
  }

  const matchedRegion = INDIA_REGION_HINTS.find(({ patterns }) =>
    patterns.some((pattern) => pattern.test(normalizedLocation))
  );

  if (matchedRegion) {
    return {
      country_code: 'IN',
      region_code: matchedRegion.code,
    };
  }

  if (/\bindia\b/i.test(normalizedLocation)) {
    return {
      country_code: 'IN',
      region_code: null,
    };
  }

  return { country_code: null, region_code: null };
}

function alignMonitoringScopeWithLocation(scope: MonitoringScope, locationInput?: string | null): MonitoringScope {
  if (scope.scope_type !== 'REGIONAL') {
    return scope;
  }

  const derived = deriveRegionalCodeFromLocation(locationInput);
  if (!derived.region_code) {
    return scope;
  }

  return {
    ...scope,
    country_code: scope.country_code || derived.country_code,
    region_code: derived.region_code,
  };
}

/** Pages that are gated by organization-type entitlements. home/profile are always accessible. */
const GATEABLE_PAGES = [
  'global_monitor', 'intelligence_center', 'alerts', 'posts', 'topics',
  'analytics', 'narrative_map', 'simulation', 'brand_protection', 'business_intelligence',
];

/** fetch wrapper with timeout (default 15s) so pages don't hang on cold starts */
async function fetchWithTimeout(url: string, timeoutMs = 15000): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    return res;
  } finally {
    clearTimeout(id);
  }
}

export default function App() {
  const [currentPage, _setCurrentPage] = useState<'home' | 'alerts' | 'posts' | 'topics' | 'analytics' | 'profile' | 'landing' | 'login' | 'signup' | 'narrative_map' | 'simulation' | 'intelligence_center' | 'global_monitor' | 'brand_protection' | 'business_intelligence'>(
    (localStorage.getItem('crisis_auth') === 'true' ? 'home' : 'landing') as any
  );

  const setCurrentPage = (page: 'home' | 'alerts' | 'posts' | 'topics' | 'analytics' | 'profile' | 'landing' | 'login' | 'signup' | 'narrative_map' | 'simulation' | 'intelligence_center' | 'global_monitor' | 'brand_protection' | 'business_intelligence' | string) => {
    _setCurrentPage(page as any);
    // Use hash-based routing for all pages — prevents GCS 404 on direct URL load
    const hash = page === 'home' ? '' : page;
    window.history.pushState({ page }, '', hash ? `#${hash}` : '/');
  };

  useEffect(() => {
    const ALL_PAGES = ['landing', 'login', 'signup', 'home', 'alerts', 'posts', 'topics', 'analytics', 'profile', 'narrative_map', 'simulation', 'intelligence_center', 'global_monitor', 'brand_protection', 'business_intelligence'];

    const handlePopState = (e: PopStateEvent) => {
      if (e.state && e.state.page) {
        _setCurrentPage(e.state.page);
      } else {
        const hash = window.location.hash.replace('#', '');
        if (hash && ALL_PAGES.includes(hash)) {
          _setCurrentPage(hash as any);
        } else {
          _setCurrentPage((localStorage.getItem('crisis_auth') === 'true' ? 'home' : 'landing') as any);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);

    // Stamp the initial history entry with the current page so the browser back button
    // always resolves within the SPA instead of escaping to the previous site.
    const initialHash = window.location.hash.replace('#', '');
    const initialPage = (initialHash && ALL_PAGES.includes(initialHash))
      ? initialHash
      : (localStorage.getItem('crisis_auth') === 'true' ? 'home' : 'landing');
    if (!window.history.state?.page) {
      window.history.replaceState({ page: initialPage }, '', window.location.href);
    }

    // Route to the correct page on initial load if the URL already has a hash
    if (initialHash && ALL_PAGES.includes(initialHash)) {
      _setCurrentPage(initialHash as any);
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('crisis_auth') === 'true');
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [monitoringScope, setMonitoringScope] = useState<MonitoringScope>(DEFAULT_MONITORING_SCOPE);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [profileLocationInput, setProfileLocationInput] = useState('');
  const [profileSetupSaving, setProfileSetupSaving] = useState(false);
  const [profileSetupError, setProfileSetupError] = useState<string | null>(null);
  const [scopeSaving, setScopeSaving] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [metrics, setMetrics] = useState<any>(null);


  // Modal & filter states
  const [selectedTopic, setSelectedTopic] = useState<any>(null);
  const [topicRelatedPosts, setTopicRelatedPosts] = useState<any[]>([]);
  const [topicPostsLoading, setTopicPostsLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [selectedAlert, setSelectedAlert] = useState<any>(null);

  const clearAuthSession = () => {
    localStorage.removeItem('crisis_auth');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('__temp_token');
  };

  useEffect(() => {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const legacyNavType = (performance as any)?.navigation?.type;
    const isReload = navEntry?.type === 'reload' || legacyNavType === 1;

    if (isReload && localStorage.getItem('crisis_auth') === 'true') {
      clearAuthSession();
      setIsAuthenticated(false);
      setAuthNotice('Session reset after reload. Please log in again.');
      setCurrentPage('login');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    let lastActivityAt = Date.now();
    const markActivity = () => {
      lastActivityAt = Date.now();
    };

    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'focus',
    ];

    events.forEach((eventName) => window.addEventListener(eventName, markActivity));

    const timer = window.setInterval(() => {
      if (Date.now() - lastActivityAt >= IDLE_LOGOUT_MS) {
        clearAuthSession();
        setIsAuthenticated(false);
        setAuthNotice('Session expired due to inactivity. Please log in again.');
        setCurrentPage('login');
      }
    }, 30000);

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, markActivity));
      clearInterval(timer);
    };
  }, [isAuthenticated]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sourcePageIndex, setSourcePageIndex] = useState(0);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [recentInquiries, setRecentInquiries] = useState<any[]>([]);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [inquiriesError, setInquiriesError] = useState<string | null>(null);
  const [inquiryStatusFilter, setInquiryStatusFilter] = useState<'all' | 'new' | 'contacted' | 'qualified' | 'closed'>('all');
  const [inquiryStartDate, setInquiryStartDate] = useState('');
  const [inquiryEndDate, setInquiryEndDate] = useState('');
  const [inquiryOffset, setInquiryOffset] = useState(0);
  const [hasMoreInquiries, setHasMoreInquiries] = useState(false);
  const [updatingInquiryId, setUpdatingInquiryId] = useState<number | null>(null);
  const [editingInquiryId, setEditingInquiryId] = useState<number | null>(null);
  const [inquiryDrafts, setInquiryDrafts] = useState<Record<number, { assignee: string; notes: string }>>({});
  const INQUIRIES_PAGE_SIZE = 8;

  // Organization-type feature entitlements (server-authoritative, fails closed)
  const { hasAccess, orgType: userOrgType, features: entitledFeatures, isAdmin: isUserAdmin } = useFeatureFlags(isAuthenticated);
  // True when the current page is gated and the user's org type lacks access
  const accessDenied = GATEABLE_PAGES.includes(currentPage) && !hasAccess(currentPage);

  const bootstrapMonitoringScope = async () => {
    let initialScope: MonitoringScope = { ...DEFAULT_MONITORING_SCOPE };
    let needsProfileSetup = false;
    let locationValue = '';

    try {
      const raw = localStorage.getItem('user_profile');
      if (raw) {
        const profile = JSON.parse(raw);
        initialScope = alignMonitoringScopeWithLocation({
          scope_type: (profile.scope_type || 'GLOBAL') as MonitoringScope['scope_type'],
          country_code: profile.country_code || null,
          region_code: profile.region_code || null,
        }, profile.location_input || '');
        needsProfileSetup = Boolean(profile.requires_profile_setup);
        locationValue = profile.location_input || '';
      }
    } catch {
      // keep defaults
    }

    setMonitoringScope(initialScope);
    setShowProfileSetup(needsProfileSetup);
    setProfileLocationInput(locationValue);

    try {
      const pref = await apiService.auth.getMonitoringPreference();
      const nextScope: MonitoringScope = alignMonitoringScopeWithLocation({
        scope_type: pref.scope_type || 'GLOBAL',
        country_code: pref.country_code || null,
        region_code: pref.region_code || null,
      }, pref.location_input || '');
      setMonitoringScope(nextScope);

      setShowProfileSetup(!Boolean(pref.profile_completed && pref.location_input));
      setProfileLocationInput(pref.location_input || '');

      const raw = localStorage.getItem('user_profile');
      if (raw) {
        const profile = JSON.parse(raw);
        const updatedProfile = {
          ...profile,
          profile_completed: Boolean(pref.profile_completed),
          requires_profile_setup: !Boolean(pref.profile_completed && pref.location_input),
          location_input: pref.location_input,
          location_country: pref.location_country,
          location_region: pref.location_region,
          scope_type: pref.scope_type,
          country_code: pref.country_code,
          region_code: pref.region_code,
        };
        localStorage.setItem('user_profile', JSON.stringify(updatedProfile));
      }
    } catch {
      // Keep local fallback if preference endpoint is unavailable
    }
  };

  const handleProfileSetupSave = async () => {
    const location = profileLocationInput.trim();
    if (!location) {
      setProfileSetupError('Location is required to complete profile setup.');
      return;
    }
    setProfileSetupSaving(true);
    setProfileSetupError(null);
    try {
      const savePreference = () => apiService.auth.updateMonitoringPreference({
        location_input: location,
      });

      let pref;
      try {
        pref = await savePreference();
      } catch (firstErr: any) {
        const msg = String(firstErr?.message || '').toLowerCase();
        const networkFailure = msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('load failed');
        if (!networkFailure) throw firstErr;
        await new Promise((resolve) => setTimeout(resolve, 400));
        pref = await savePreference();
      }

      setMonitoringScope(alignMonitoringScopeWithLocation({
        scope_type: pref.scope_type || 'GLOBAL',
        country_code: pref.country_code || null,
        region_code: pref.region_code || null,
      }, pref.location_input || location));
      setShowProfileSetup(false);

      const raw = localStorage.getItem('user_profile');
      if (raw) {
        const profile = JSON.parse(raw);
        localStorage.setItem('user_profile', JSON.stringify({
          ...profile,
          profile_completed: true,
          requires_profile_setup: false,
          location_input: pref.location_input,
          location_country: pref.location_country,
          location_region: pref.location_region,
          scope_type: pref.scope_type,
          country_code: pref.country_code,
          region_code: pref.region_code,
        }));
      }
    } catch (err: any) {
      if (String(err?.message || '').includes('401')) {
        clearAuthSession();
        setIsAuthenticated(false);
        setAuthNotice('Session expired. Please log in again.');
        setCurrentPage('login');
        setProfileSetupError('Session expired. Please log in again.');
        return;
      }
      if (String(err?.message || '').toLowerCase().includes('failed to fetch')) {
        setProfileSetupError('Network issue while saving location. Please retry.');
      } else {
        setProfileSetupError(err?.message || 'Failed to save profile setup.');
      }
    } finally {
      setProfileSetupSaving(false);
    }
  };

  const handleMonitoringScopeSave = async () => {
    setScopeSaving(true);
    setScopeError(null);
    try {
      const nextScope = alignMonitoringScopeWithLocation(monitoringScope, profileLocationInput);
      const pref = await apiService.auth.updateMonitoringPreference({
        scope_type: nextScope.scope_type,
        country_code: nextScope.scope_type === 'NATIONAL' ? (nextScope.country_code || null) : null,
        region_code: nextScope.scope_type === 'REGIONAL' ? (nextScope.region_code || null) : null,
      });

      setMonitoringScope(alignMonitoringScopeWithLocation({
        scope_type: pref.scope_type || 'GLOBAL',
        country_code: pref.country_code || null,
        region_code: pref.region_code || null,
      }, pref.location_input || profileLocationInput));

      const raw = localStorage.getItem('user_profile');
      if (raw) {
        const profile = JSON.parse(raw);
        localStorage.setItem('user_profile', JSON.stringify({
          ...profile,
          scope_type: pref.scope_type,
          country_code: pref.country_code,
          region_code: pref.region_code,
        }));
      }
    } catch (err: any) {
      setScopeError(err?.message || 'Failed to update monitoring scope.');
    } finally {
      setScopeSaving(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      bootstrapMonitoringScope();
    }
  }, [isAuthenticated]);

  const loadRecentInquiries = async () => {
    const token = localStorage.getItem('access_token');
    if (!token || !isUserAdmin) return;
    setInquiriesLoading(true);
    setInquiriesError(null);
    try {
      const params = new URLSearchParams({
        limit: INQUIRIES_PAGE_SIZE.toString(),
        offset: inquiryOffset.toString(),
      });
      if (inquiryStatusFilter !== 'all') {
        params.set('status_filter', inquiryStatusFilter);
      }
      if (inquiryStartDate) {
        params.set('start_date', inquiryStartDate);
      }
      if (inquiryEndDate) {
        params.set('end_date', inquiryEndDate);
      }
      const response = await fetch(`${API_BASE_URL}/api/public/inquiries/admin?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || 'Failed to load inquiries.');
      }
      const data = await response.json();
      const rows = Array.isArray(data) ? data : [];
      setRecentInquiries(rows);
      setHasMoreInquiries(rows.length === INQUIRIES_PAGE_SIZE);
    } catch (error: any) {
      setInquiriesError(error?.message || 'Failed to load inquiries.');
    } finally {
      setInquiriesLoading(false);
    }
  };

  const handleInquiryStatusUpdate = async (inquiryId: number, nextStatus: 'contacted' | 'qualified' | 'closed') => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    setUpdatingInquiryId(inquiryId);
    setInquiriesError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/inquiries/admin/${inquiryId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || 'Failed to update inquiry status.');
      }

      const updated = await response.json();
      setRecentInquiries((prev) => prev.map((item) => (item.id === inquiryId ? updated : item)));
      if (inquiryStatusFilter !== 'all' && inquiryStatusFilter !== updated.status) {
        await loadRecentInquiries();
      }
    } catch (error: any) {
      setInquiriesError(error?.message || 'Failed to update inquiry status.');
    } finally {
      setUpdatingInquiryId(null);
    }
  };

  const handleInquiryDraftChange = (inquiryId: number, field: 'assignee' | 'notes', value: string) => {
    setInquiryDrafts((prev) => ({
      ...prev,
      [inquiryId]: {
        assignee: prev[inquiryId]?.assignee ?? '',
        notes: prev[inquiryId]?.notes ?? '',
        [field]: value,
      },
    }));
  };

  const startInquiryEdit = (inquiry: any) => {
    setEditingInquiryId(inquiry.id);
    setInquiryDrafts((prev) => ({
      ...prev,
      [inquiry.id]: {
        assignee: inquiry.assignee || '',
        notes: inquiry.notes || '',
      },
    }));
  };

  const saveInquiryDetails = async (inquiryId: number) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const draft = inquiryDrafts[inquiryId] || { assignee: '', notes: '' };
    setUpdatingInquiryId(inquiryId);
    setInquiriesError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/inquiries/admin/${inquiryId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ assignee: draft.assignee, notes: draft.notes }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || 'Failed to save inquiry follow-up details.');
      }
      const updated = await response.json();
      setRecentInquiries((prev) => prev.map((item) => (item.id === inquiryId ? updated : item)));
      setEditingInquiryId(null);
    } catch (error: any) {
      setInquiriesError(error?.message || 'Failed to save inquiry follow-up details.');
    } finally {
      setUpdatingInquiryId(null);
    }
  };

  const downloadInquiryExport = async (format: 'csv' | 'json') => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    setInquiriesError(null);
    try {
      const params = new URLSearchParams({ limit: '5000' });
      if (inquiryStatusFilter !== 'all') {
        params.set('status_filter', inquiryStatusFilter);
      }
      if (inquiryStartDate) {
        params.set('start_date', inquiryStartDate);
      }
      if (inquiryEndDate) {
        params.set('end_date', inquiryEndDate);
      }
      const response = await fetch(`${API_BASE_URL}/api/export/inquiries.${format}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || `Failed to export inquiries as ${format.toUpperCase()}.`);
      }
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = Object.assign(document.createElement('a'), {
        href,
        download: `sales-inquiries.${format}`,
      });
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (error: any) {
      setInquiriesError(error?.message || `Failed to export inquiries as ${format.toUpperCase()}.`);
    }
  };

  useEffect(() => {
    if (currentPage === 'profile' && isAuthenticated && isUserAdmin) {
      loadRecentInquiries();
    }
  }, [currentPage, isAuthenticated, isUserAdmin, inquiryStatusFilter, inquiryStartDate, inquiryEndDate, inquiryOffset]);

  const resetPosts = async () => {
    setResetting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/reset-posts`, { method: 'POST' });
      if (res.ok) {
        setMetrics(null);
        setAlerts([]);
      }
    } catch (err) {
      console.error('Reset failed:', err);
    } finally {
      setResetting(false);
      setResetConfirm(false);
    }
  };

  // 7-day post count reset banner
  const [show7DayBanner, setShow7DayBanner] = useState(false);
  useEffect(() => {
    const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
    const lastReset = Number(localStorage.getItem('post_count_reset_ts') || '0');
    const now = Date.now();
    if (now - lastReset >= SEVEN_DAYS_MS) {
      setShow7DayBanner(true);
    }
    // Check again every hour
    const interval = setInterval(() => {
      const lr = Number(localStorage.getItem('post_count_reset_ts') || '0');
      if (Date.now() - lr >= SEVEN_DAYS_MS) setShow7DayBanner(true);
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  const dismiss7DayBanner = () => {
    localStorage.setItem('post_count_reset_ts', String(Date.now()));
    setShow7DayBanner(false);
  };

  const activeScopeLabel = monitoringScope.scope_type === 'NATIONAL'
    ? `National${monitoringScope.country_code ? ` (${monitoringScope.country_code})` : ''}`
    : monitoringScope.scope_type === 'REGIONAL'
    ? `Regional${monitoringScope.region_code ? ` (${monitoringScope.region_code})` : ''}`
    : 'Global';

  const platformName = (p: string) => ({
    // ── Existing social & API sources ──
    'news_org': 'NewsAPI.org', 'news_ai': 'NewsAPI.ai', 'twitter': 'Twitter / X', 'youtube': 'YouTube',
    'reddit': 'Reddit', 'facebook': 'Facebook', 'guardian': 'The Guardian', 'bbc': 'BBC News',
    'reuters': 'Reuters', 'aljazeera': 'Al Jazeera', 'hindu': 'The Hindu', 'ndtv': 'NDTV',
    'rss': 'RSS Feeds', 'telegram': 'Telegram', 'mastodon': 'Mastodon', 'factcheck': 'Fact-Check',
    // ── Phase 5A: New sources ──
    'gdelt': 'GDELT', 'apnews': 'AP News', 'afp': 'AFP', 'dw': 'DW News',
    'snopes': 'Snopes', 'afp_factcheck': 'AFP FC', 'fullfact': 'FullFact',
    'leadstories': 'Lead Stories', 'politifact': 'PolitiFact', 'reuters_factcheck': 'Reuters FC',
    'ani': 'ANI', 'pib': 'PIB', 'ddnews': 'DD News', 'mygov': 'MyGov', 'mea': 'MEA India', 'mha': 'MHA India',
    'hindustantimes': 'HT', 'timesofindia': 'TOI', 'economictimes': 'Economic Times',
    'indiatoday': 'India Today', 'thewire': 'The Wire', 'scrollin': 'Scroll.in',
    'theprint': 'The Print', 'firstpost': 'Firstpost', 'deccanherald': 'Deccan Herald',
    'deccanchronicle': 'Deccan Chronicle', 'tribuneindia': 'Tribune India',
    'thestatesman': 'The Statesman', 'businessstandard': 'Business Std', 'financialexpress': 'Financial Express', 'livemint': 'Mint',
    'bhaskar': 'Dainik Bhaskar', 'amarujala': 'Amar Ujala', 'navbharattimes': 'Nav Bharat Times',
    'jagran': 'Jagran', 'punjabkesari': 'Punjab Kesari', 'lokmat': 'Lokmat',
    'eenadu': 'Eenadu', 'dinamalar': 'Dinamalar', 'mathrubhumi': 'Mathrubhumi', 'anandabazar': 'Anandabazar',
    'altnews': 'Alt News', 'boomlive': 'Boom Live', 'ndtv_factcheck': 'NDTV FC',
    'indiatoday_factcheck': 'IT Fact Check', 'vishvasnews': 'Vishvas News',
    'factly': 'Factly', 'webqoof': 'WebQoof',
    'krebsonsecurity': 'Krebs Sec', 'hackernews': 'Hacker News', 'techcrunch': 'TechCrunch', 'wired': 'Wired',
    'dawn': 'Dawn (PK)', 'ispr': 'ISPR', 'thedailystar': 'Daily Star BD',
  } as Record<string, string>)[p] || p;

  const platformIcon = (p: string): string => ({
    // Social & API
    twitter: '𝕏', reddit: '🔴', youtube: '▶️', news_ai: '🤖', facebook: '📘',
    guardian: '🛡️', telegram: '✈️', mastodon: '🐘', bbc: 'MB', reuters: '🌐',
    factcheck: '✅', gdelt: '🌍',
    // International wire & FC
    apnews: '📡', afp: '🗞️', dw: '🌐', aljazeera: '📺',
    snopes: '🔍', afp_factcheck: '✔️', fullfact: '✔️', leadstories: '📋', politifact: '⚖️', reuters_factcheck: '✔️',
    // India wire & gov
    ani: '📡', pib: '🏛️', ddnews: '📺', mygov: '🏛️', mea: '🌐', mha: '🔐',
    // India English
    hindu: '📰', ndtv: '📺', hindustantimes: '📰', timesofindia: '📰', economictimes: '💹',
    indiatoday: '📰', thewire: '🔌', scrollin: '📜', theprint: '🖨️', firstpost: '📰',
    deccanherald: '📰', deccanchronicle: '📰', tribuneindia: '📰', thestatesman: '📰',
    businessstandard: '💼', financialexpress: '💰', livemint: '🪙',
    // India Regional
    bhaskar: '📰', amarujala: '📰', navbharattimes: '📰', jagran: '📰', punjabkesari: '📰',
    lokmat: '📰', eenadu: '📰', dinamalar: '📰', mathrubhumi: '📰', anandabazar: '📰',
    // India Fact-check
    altnews: '🔎', boomlive: '💥', ndtv_factcheck: '✔️', indiatoday_factcheck: '✔️',
    vishvasnews: '🔭', factly: '📊', webqoof: '❓',
    // Tech
    krebsonsecurity: '🔐', hackernews: '💻', techcrunch: '⚡', wired: '📡',
    // South Asia
    dawn: '🌅', ispr: '🎖️', thedailystar: '⭐',
  } as Record<string, string>)[p] ?? '📰';

  // All source platforms for the carousel (12 per page)
  const ALL_SOURCE_PLATFORMS = [
    // Page 1 — Social & API sources
    'news_org', 'news_ai', 'twitter', 'youtube', 'reddit', 'facebook', 'telegram', 'mastodon', 'gdelt', 'factcheck', 'guardian', 'bbc',
    // Page 2 — International wire & fact-check
    'reuters', 'apnews', 'afp', 'aljazeera', 'dw', 'snopes', 'afp_factcheck', 'fullfact', 'leadstories', 'politifact', 'reuters_factcheck', 'ani',
    // Page 3 — India wire, gov & nationals
    'pib', 'ddnews', 'mygov', 'mea', 'mha', 'hindu', 'ndtv', 'hindustantimes', 'timesofindia', 'economictimes', 'indiatoday', 'thewire',
    // Page 4 — India English (more) + business
    'scrollin', 'theprint', 'firstpost', 'deccanherald', 'deccanchronicle', 'tribuneindia', 'thestatesman', 'businessstandard', 'financialexpress', 'livemint', 'bhaskar', 'amarujala',
    // Page 5 — India Regional & Fact-check
    'navbharattimes', 'jagran', 'punjabkesari', 'lokmat', 'eenadu', 'dinamalar', 'mathrubhumi', 'anandabazar', 'altnews', 'boomlive', 'ndtv_factcheck', 'indiatoday_factcheck',
    // Page 6 — More FC, Tech & South Asia
    'vishvasnews', 'factly', 'webqoof', 'krebsonsecurity', 'hackernews', 'techcrunch', 'wired', 'dawn', 'ispr', 'thedailystar',
  ];
  const SOURCES_PER_PAGE = 12;
  const totalSourcePages = Math.ceil(ALL_SOURCE_PLATFORMS.length / SOURCES_PER_PAGE);

  const PLATFORM_BRAND: Record<string, { hex: string; active: boolean }> = {
    // ── Existing ──
    news_org: { hex: '#22c55e', active: true  }, news_ai:  { hex: '#8b5cf6', active: true  },
    twitter:  { hex: '#1d9bf0', active: true  }, youtube:  { hex: '#ef4444', active: true  },
    reddit:   { hex: '#f97316', active: false }, facebook: { hex: '#3b82f6', active: false },
    guardian: { hex: '#06b6d4', active: true  }, bbc:      { hex: '#f43f5e', active: true  },
    reuters:  { hex: '#f59e0b', active: true  }, telegram: { hex: '#38bdf8', active: true  },
    mastodon: { hex: '#818cf8', active: true  }, factcheck:{ hex: '#4ade80', active: true  },
    // ── Phase 5A: International ──
    gdelt:    { hex: '#22d3ee', active: true  }, apnews:   { hex: '#60a5fa', active: true  },
    afp:      { hex: '#a78bfa', active: true  }, dw:       { hex: '#f87171', active: true  },
    aljazeera:{ hex: '#fbbf24', active: true  },
    snopes:       { hex: '#34d399', active: true  }, afp_factcheck: { hex: '#6ee7b7', active: true },
    fullfact:     { hex: '#4ade80', active: true  }, leadstories:   { hex: '#86efac', active: true },
    politifact:   { hex: '#a3e635', active: true  }, reuters_factcheck: { hex: '#d4d4a8', active: true },
    // ── Phase 5A: India Wire & Gov ──
    ani:  { hex: '#fb923c', active: true  }, pib:    { hex: '#60a5fa', active: true  },
    ddnews: { hex: '#f472b6', active: true }, mygov:  { hex: '#34d399', active: true  },
    mea:  { hex: '#a78bfa', active: true  }, mha:    { hex: '#f87171', active: true  },
    // ── Phase 5A: India English ──
    hindu:          { hex: '#f97316', active: true }, ndtv:     { hex: '#ef4444', active: true },
    hindustantimes: { hex: '#f59e0b', active: true }, timesofindia: { hex: '#fb923c', active: true },
    economictimes:  { hex: '#22c55e', active: true }, indiatoday:   { hex: '#3b82f6', active: true },
    thewire:        { hex: '#8b5cf6', active: true }, scrollin:     { hex: '#06b6d4', active: true },
    theprint:       { hex: '#a78bfa', active: true }, firstpost:    { hex: '#f43f5e', active: true },
    deccanherald:   { hex: '#d97706', active: true }, deccanchronicle: { hex: '#b45309', active: true },
    tribuneindia:   { hex: '#7c3aed', active: true }, thestatesman: { hex: '#6d28d9', active: true },
    businessstandard: { hex: '#059669', active: true }, financialexpress: { hex: '#10b981', active: true },
    livemint:       { hex: '#34d399', active: true },
    // ── Phase 5A: India Regional ──
    bhaskar:     { hex: '#f59e0b', active: true }, amarujala:     { hex: '#f97316', active: true },
    navbharattimes: { hex: '#ef4444', active: true }, jagran:     { hex: '#dc2626', active: true },
    punjabkesari:{ hex: '#b91c1c', active: true }, lokmat:        { hex: '#c2410c', active: true },
    eenadu:      { hex: '#d97706', active: true }, dinamalar:     { hex: '#ca8a04', active: true },
    mathrubhumi: { hex: '#a16207', active: true }, anandabazar:   { hex: '#92400e', active: true },
    // ── Phase 5A: India Fact-check ──
    altnews:           { hex: '#4ade80', active: true }, boomlive:           { hex: '#86efac', active: true },
    ndtv_factcheck:    { hex: '#22c55e', active: true }, indiatoday_factcheck: { hex: '#16a34a', active: true },
    vishvasnews:       { hex: '#15803d', active: true }, factly:             { hex: '#166534', active: true },
    webqoof:           { hex: '#14532d', active: true },
    // ── Phase 5A: Tech & Cybersecurity ──
    krebsonsecurity: { hex: '#c084fc', active: true }, hackernews: { hex: '#818cf8', active: true },
    techcrunch:      { hex: '#fb923c', active: true }, wired:      { hex: '#94a3b8', active: true },
    // ── Phase 5A: South Asia ──
    dawn:        { hex: '#38bdf8', active: true }, ispr:        { hex: '#34d399', active: true },
    thedailystar:{ hex: '#fbbf24', active: true },
  };
  const riskColor = (r: number) => r > 0.7 ? 'text-rose-400' : r > 0.3 ? 'text-amber-400' : 'text-emerald-400';
  const riskBg = (r: number) => r > 0.7 ? 'bg-rose-500/10' : r > 0.3 ? 'bg-amber-500/10' : 'bg-emerald-500/10';
  const riskGradient = (r: number) => r > 0.7 ? 'bg-gradient-to-r from-rose-500 to-red-500' : r > 0.5 ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-cyan-500 to-blue-500';

  const safeTime = (ts: string | undefined | null) => {
    if (!ts) return 'Just now';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return 'Just now';
      const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
      return d.toLocaleDateString();
    } catch { return 'Just now'; }
  };

  const safeRisk = (r: any): number => {
    const n = Number(r);
    return isNaN(n) ? 0 : Math.min(1, Math.max(0, n));
  };

  const MOCK_CRITICAL_ALERTS = [
    { id: 'm1', severity: 'CRITICAL', type: 'MISINFORMATION', message: 'Coordinated bot network amplifying election fraud claims — 3,400+ posts in 2 hours', platform: 'Twitter/X', timestamp: new Date(Date.now() - 8 * 60000).toISOString(), risk_score: 0.94 },
    { id: 'm2', severity: 'CRITICAL', type: 'BOT_NETWORK', message: 'AI-generated deepfake video of government official spreading on Telegram channels', platform: 'Telegram', timestamp: new Date(Date.now() - 23 * 60000).toISOString(), risk_score: 0.91 },
    { id: 'm3', severity: 'CRITICAL', type: 'VIRAL_SPREAD', message: 'Unverified health claim gaining critical traction — 1,200 shares in under 45 minutes', platform: 'Telegram', timestamp: new Date(Date.now() - 41 * 60000).toISOString(), risk_score: 0.87 },
    { id: 'm4', severity: 'CRITICAL', type: 'NARRATIVE_SHIFT', message: 'Cross-platform coordinated push: anti-vaccination narrative spike detected on 4 platforms', platform: 'Multi-platform', timestamp: new Date(Date.now() - 67 * 60000).toISOString(), risk_score: 0.83 },
    { id: 'm5', severity: 'CRITICAL', type: 'DEEP_FAKE', message: 'Fabricated viral post claiming hospital infrastructure failure — origin traced to single bot cluster', platform: 'YouTube', timestamp: new Date(Date.now() - 94 * 60000).toISOString(), risk_score: 0.88 },
  ];

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setAlertsLoading(true);
        const data = await apiService.alerts.getCriticalAlerts(5, monitoringScope);
        setAlerts(Array.isArray(data) ? data.slice(0, 5) : []);
      } catch (error) {
        console.error('Error fetching alerts:', error);
      } finally {
        setAlertsLoading(false);
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 10000);
    return () => clearInterval(interval);
  }, [monitoringScope]);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await apiService.metrics.getGlobalMetrics(monitoringScope);
        setMetrics(data);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      }
    };
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 15000);
    return () => clearInterval(interval);
  }, [monitoringScope]);



  // Fetch related posts when a topic is selected (for modal)
  useEffect(() => {
    if (selectedTopic) {
      const fetchTopicPosts = async () => {
        setTopicPostsLoading(true);
        try {
          const response = await fetchWithTimeout(
            `${API_BASE_URL}/api/posts/search?q=${encodeURIComponent(selectedTopic.keyword)}&limit=20` +
            `&scope_type=${encodeURIComponent(monitoringScope.scope_type)}` +
            `${monitoringScope.country_code ? `&country_code=${encodeURIComponent(monitoringScope.country_code)}` : ''}` +
            `${monitoringScope.region_code ? `&region_code=${encodeURIComponent(monitoringScope.region_code)}` : ''}`
          );
          if (response.ok) {
            const data = await response.json();
            setTopicRelatedPosts(Array.isArray(data) ? data : []);
          }
        } catch (error) {
          console.error('Error fetching topic posts:', error);
          setTopicRelatedPosts([]);
        } finally {
          setTopicPostsLoading(false);
        }
      };
      fetchTopicPosts();
    }
  }, [selectedTopic, monitoringScope]);

  if (['landing', 'login', 'signup'].includes(currentPage)) {
    return (
      <>
        <Landing setPage={setCurrentPage} />
        {currentPage === 'login' && (
          <Login 
            setPage={setCurrentPage} 
            authNotice={authNotice}
            onAuth={() => {
              setIsAuthenticated(true);
              setAuthNotice(null);
              localStorage.setItem('crisis_auth', 'true');
              bootstrapMonitoringScope();
              // Route to the agency's default landing page (display-only; access is
              // still enforced by entitlements — landings are always entitled pages).
              let landing = 'home';
              try {
                const raw = localStorage.getItem('user_profile');
                const orgType = raw ? (JSON.parse(raw)?.organization_type as string | undefined) : undefined;
                landing = getAgencyProfile(orgType).landing || 'home';
              } catch {
                landing = 'home';
              }
              setCurrentPage(landing);
            }} 
          />
        )}
        {currentPage === 'signup' && <Signup setPage={setCurrentPage} />}
      </>
    );
  }

  // If trying to access dashboard without auth, kick back to landing
  if (!isAuthenticated) {
    if (!['landing', 'login', 'signup'].includes(currentPage)) {
      setCurrentPage(authNotice ? 'login' : 'landing');
    }
    return null;
  }

  return (
    <div className="flex h-screen bg-zinc-950 text-zinc-50 overflow-hidden font-sans selection:bg-cyan-500/30">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[590]"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      <motion.nav 
        initial={{ x: -288 }}
        animate={{ x: sidebarOpen ? 0 : -288 }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        className="fixed z-[600] h-full w-72 flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #0a0a0f 0%, #0d0d14 60%, #0a0a0f 100%)',
          borderRight: '1px solid rgba(99,102,241,0.15)',
          boxShadow: '4px 0 40px rgba(0,0,0,0.8), inset -1px 0 0 rgba(99,102,241,0.08)',
        }}
      >
        {/* Logo / brand */}
        <div className="px-5 py-5 flex items-center gap-3.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="relative">
            <div className="absolute inset-0 rounded-xl bg-cyan-500 blur-md opacity-40" />
            <div className="relative p-2.5 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-xl shadow-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[17px] font-black text-white tracking-tight leading-none truncate">{getAgencyProfile(userOrgType).brand}</h2>
            <p className="text-xs text-cyan-400 font-bold tracking-[0.25em] uppercase mt-0.5 opacity-80">Intelligence</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-white transition-all hover:bg-white/5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-5 px-3">
          <p className="text-xs font-extrabold tracking-[0.22em] uppercase px-3 mb-4 text-slate-400">Navigation</p>
          <ul className="space-y-0.5">
            {[
              { id: 'home',                 label: 'Dashboard Overview',    icon: LayoutDashboard, color: '#06b6d4', glow: 'rgba(6,182,212,0.15)'     },
              { id: 'global_monitor',       label: 'Global Monitor',        icon: Radio,           color: '#22c55e', glow: 'rgba(34,197,94,0.15)'      },
              { id: 'intelligence_center',  label: 'Intelligence Center',   icon: ShieldAlert,     color: '#8b5cf6', glow: 'rgba(139,92,246,0.15)'     },
              { id: 'alerts',               label: 'Alert Management',      icon: BellRing,        color: '#f43f5e', glow: 'rgba(244,63,94,0.15)'      },
              { id: 'posts',                label: 'Post Investigation',    icon: FileSearch,      color: '#f59e0b', glow: 'rgba(245,158,11,0.15)'     },
              { id: 'topics',               label: 'Trending Topics',       icon: TrendingUp,      color: '#a78bfa', glow: 'rgba(167,139,250,0.15)'     },
              { id: 'analytics',            label: 'Analytics Engine',      icon: BarChart3,       color: '#38bdf8', glow: 'rgba(56,189,248,0.15)'      },
              { id: 'narrative_map',        label: 'Narrative Mapping',     icon: Activity,        color: '#fb923c', glow: 'rgba(251,146,60,0.15)'      },
              { id: 'simulation',           label: 'Simulation Engine',     icon: TrendingUp,      color: '#34d399', glow: 'rgba(52,211,153,0.15)'      },
              { id: 'brand_protection',     label: 'Brand Protection',      icon: ShieldCheck,     color: '#60a5fa', glow: 'rgba(96,165,250,0.15)'      },
              { id: 'business_intelligence',label: 'Business Intelligence', icon: Briefcase,       color: '#fbbf24', glow: 'rgba(251,191,36,0.15)'      },
            ].filter((item) => hasAccess(item.id)).map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => { setCurrentPage(item.id as any); setSidebarOpen(false); }}
                    className="w-full flex items-center gap-3.5 px-3 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden"
                    style={isActive ? {
                      background: `linear-gradient(135deg, ${item.glow}, rgba(255,255,255,0.02))`,
                      border: `1px solid ${item.color}30`,
                      boxShadow: `0 0 20px ${item.glow}, inset 0 1px 0 rgba(255,255,255,0.05)`,
                    } : {}}
                  >
                    {/* left accent bar on active */}
                    {isActive && (
                      <div className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full" style={{ background: item.color, boxShadow: `0 0 8px ${item.color}` }} />
                    )}
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
                      style={isActive
                        ? { background: item.color + '20', border: `1px solid ${item.color}40` }
                        : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }
                      }
                    >
                      <Icon
                        className="w-4 h-4 transition-all"
                        style={{ color: isActive ? item.color : '#94a3b8' }}
                      />
                    </div>
                    <span
                      className="font-semibold text-[15px] tracking-wide leading-none transition-colors"
                      style={{ color: isActive ? '#ffffff' : '#94a3b8' }}
                    >
                      {getAgencyLabel(userOrgType, item.id, item.label)}
                    </span>
                    {isActive && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: item.color, boxShadow: `0 0 6px ${item.color}` }} />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <button 
            onClick={() => { setCurrentPage('profile'); setSidebarOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all"
            style={currentPage === 'profile'
              ? { background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)', boxShadow: '0 0 16px rgba(139,92,246,0.1)' }
              : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }
            }
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-purple-500 blur opacity-30" />
              <div className="relative w-9 h-9 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg">
                <User className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="text-left">
              <p className="text-[14px] font-bold text-white">Session Active</p>
              <p className="text-xs text-emerald-400 font-bold tracking-wide">● Authenticated</p>
            </div>
          </button>
        </div>
      </motion.nav>

      <main className="flex-1 overflow-y-auto relative bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-zinc-950 to-zinc-950 p-3 md:p-5"
        onClick={(e) => { if (sidebarOpen) { e.stopPropagation(); setSidebarOpen(false); } }}
      >
        {/* Header with hamburger — always visible */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:text-white transition-colors">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-bold text-white">{getAgencyProfile(userOrgType).brand}</span>
          </div>
        </div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none" />

        {/* 7-day post count reset banner */}
        {show7DayBanner && (
          <div className="w-full mb-4 relative z-20">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-5 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-amber-400 text-xl">🔄</span>
                <div>
                  <p className="text-amber-300 font-bold text-sm">7-Day Data Cycle Complete</p>
                  <p className="text-zinc-400 text-xs mt-0.5">The post analysis window has elapsed. Post counts will reset on your next system sync. Historical data is archived.</p>
                </div>
              </div>
              <button onClick={dismiss7DayBanner} className="shrink-0 px-3 py-1.5 text-xs font-bold bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 rounded-lg transition-all">
                Acknowledge & Reset
              </button>
            </div>
          </div>
        )}

        {/* Persistent scope controls visible on all dashboard pages */}
        <div className="mb-5 flex flex-wrap items-end gap-3 p-3 rounded-xl bg-zinc-900/40 border border-zinc-800/60">
          <div className="mr-2">
            <p className="text-[11px] text-zinc-500 uppercase tracking-widest">Active Scope</p>
            <p className="text-sm font-semibold text-cyan-300">{activeScopeLabel}</p>
          </div>

          <div>
            <label className="block text-[11px] text-zinc-500 uppercase tracking-widest mb-1">Monitoring Scope</label>
            <select
              value={monitoringScope.scope_type}
              onChange={(e) => setMonitoringScope((prev) => ({
                ...prev,
                scope_type: e.target.value as MonitoringScope['scope_type'],
                country_code: e.target.value === 'NATIONAL' ? prev.country_code : null,
                region_code: e.target.value === 'REGIONAL' ? prev.region_code : null,
              }))}
              className="bg-zinc-800/70 border border-zinc-700/60 rounded-lg px-3 py-2 text-sm text-zinc-200"
            >
              <option value="GLOBAL">Global</option>
              <option value="NATIONAL">National</option>
              <option value="REGIONAL">Regional</option>
            </select>
          </div>

          {monitoringScope.scope_type === 'NATIONAL' && (
            <div>
              <label className="block text-[11px] text-zinc-500 uppercase tracking-widest mb-1">Country Code</label>
              <input
                value={monitoringScope.country_code || ''}
                onChange={(e) => setMonitoringScope((prev) => ({ ...prev, country_code: e.target.value.toUpperCase() || null }))}
                placeholder="IN"
                maxLength={2}
                className="w-24 bg-zinc-800/70 border border-zinc-700/60 rounded-lg px-3 py-2 text-sm text-zinc-200"
              />
            </div>
          )}

          {monitoringScope.scope_type === 'REGIONAL' && (
            <div>
              <label className="block text-[11px] text-zinc-500 uppercase tracking-widest mb-1">Region Code</label>
              <input
                value={monitoringScope.region_code || ''}
                onChange={(e) => setMonitoringScope((prev) => ({ ...prev, region_code: e.target.value.toUpperCase() || null }))}
                placeholder="IN-DL"
                className="w-28 bg-zinc-800/70 border border-zinc-700/60 rounded-lg px-3 py-2 text-sm text-zinc-200"
              />
            </div>
          )}

          <button
            onClick={handleMonitoringScopeSave}
            disabled={scopeSaving}
            className="px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-sm font-semibold hover:bg-cyan-500/30 disabled:opacity-50"
          >
            {scopeSaving ? 'Saving…' : 'Apply Scope'}
          </button>

          {scopeError && <p className="text-xs text-rose-400">{scopeError}</p>}
        </div>

        <AnimatePresence mode="wait">
          {currentPage === 'home' && (
            <motion.div
              key="home"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full relative z-10"
            >
              <div className="mb-8 md:mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                  <h1 className="text-xl md:text-3xl font-bold tracking-tight text-white mb-2">
                    Global Command Center
                  </h1>
                  <p className="text-zinc-300">
                    Real-time monitoring and analysis of misinformation across networks
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-zinc-300 bg-zinc-900/50 px-4 py-2 rounded-full border border-zinc-700/60">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  System Active
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {[
                  { title: 'Posts Analyzed', value: metrics?.total_posts_monitored || '0', icon: FileText, color: 'text-blue-400', bg: 'bg-blue-400/10' },
                  { title: 'Critical Threats', value: metrics?.critical_alerts || '0', icon: ShieldAlert, color: 'text-rose-400', bg: 'bg-rose-400/10' },
                  { title: 'Misinfo Detected', value: metrics?.misinformation_detected || '0', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400/10' },
                  { title: 'System Health', value: metrics?.system_health || '100%', icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
                ].map((metric, idx) => {
                  const Icon = metric.icon;
                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      key={idx}
                      className="glass-panel p-6 rounded-2xl relative overflow-hidden group"
                    >
                      <div className={`absolute -right-6 -top-6 w-24 h-24 ${metric.bg} rounded-full blur-2xl group-hover:bg-opacity-20 transition-all`} />
                      <div className="flex justify-between items-start mb-4 relative z-10">
                        <div className={`p-3 rounded-xl ${metric.bg}`}>
                          <Icon className={`w-6 h-6 ${metric.color}`} />
                        </div>
                        {idx === 0 && (
                          <div className="flex items-center gap-1">
                            {resetConfirm ? (
                              <>
                                <button
                                  onClick={resetPosts}
                                  disabled={resetting}
                                  className="text-xs px-2 py-1 rounded bg-rose-500/20 border border-rose-500/40 text-rose-400 hover:bg-rose-500/30 font-semibold transition-all disabled:opacity-50"
                                >{resetting ? '…' : 'Confirm'}</button>
                                <button
                                  onClick={() => setResetConfirm(false)}
                                  className="text-xs px-2 py-1 rounded bg-zinc-800 border border-zinc-700 text-zinc-400 hover:text-white font-semibold transition-all"
                                >Cancel</button>
                              </>
                            ) : (
                              <button
                                onClick={() => setResetConfirm(true)}
                                title="Reset all posts — start fresh with real data"
                                className="text-zinc-400 hover:text-rose-400 transition-colors p-1.5 rounded-lg hover:bg-rose-500/10"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="relative z-10">
                        <h3 className="text-3xl font-bold text-white mb-1 tracking-tight">{metric.value}</h3>
                        <p className="text-sm font-medium text-zinc-400">{metric.title}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Global Threat Index */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="glass-panel p-5 rounded-2xl mb-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-bold text-white uppercase tracking-widest flex items-center gap-2">
                    <Radio className="w-4 h-4 text-cyan-400 animate-pulse" /> Global Threat Index
                  </h3>
                  <span className="flex items-center gap-1.5 text-xs font-bold text-cyan-400 bg-cyan-500/10 border border-cyan-500/25 px-2.5 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse inline-block" />
                    LIVE
                  </span>
                </div>
                <div className="flex gap-1 h-5 rounded-lg overflow-hidden mb-3">
                  <motion.div initial={{ width: 0 }} animate={{ width: '25%' }} transition={{ duration: 1 }} className="bg-emerald-500 rounded-l-lg" />
                  <motion.div initial={{ width: 0 }} animate={{ width: '20%' }} transition={{ duration: 1, delay: 0.1 }} className="bg-yellow-500" />
                  <motion.div initial={{ width: 0 }} animate={{ width: '25%' }} transition={{ duration: 1, delay: 0.2 }} className="bg-amber-500" />
                  <motion.div initial={{ width: 0 }} animate={{ width: '18%' }} transition={{ duration: 1, delay: 0.3 }} className="bg-rose-500" />
                  <motion.div initial={{ width: 0 }} animate={{ width: '12%' }} transition={{ duration: 1, delay: 0.4 }} className="bg-red-600 rounded-r-lg" />
                </div>
                <div className="flex justify-between text-xs font-bold tracking-widest uppercase">
                  <span className="text-emerald-400">LOW</span>
                  <span className="text-yellow-400">MODERATE</span>
                  <span className="text-amber-400">ELEVATED</span>
                  <span className="text-rose-400">HIGH</span>
                  <span className="text-red-400">CRITICAL</span>
                </div>
              </motion.div>

              {/* Platform Status — Animated Data Source Carousel */}
              <div className="mb-6">
                {/* Header row: arrows + page label */}
                <div className="flex items-center gap-2 mb-3">
                  {/* Prev arrow */}
                  <button
                    onClick={() => setSourcePageIndex(prev => Math.max(0, prev - 1))}
                    disabled={sourcePageIndex === 0}
                    className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg border border-zinc-700/60 bg-zinc-900/60 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
                    aria-label="Previous sources"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page label */}
                  <span className="text-xs font-bold text-zinc-300 tracking-widest uppercase select-none">
                    Sources&nbsp;
                    <span className="text-white">
                      {sourcePageIndex * SOURCES_PER_PAGE + 1}–{Math.min((sourcePageIndex + 1) * SOURCES_PER_PAGE, ALL_SOURCE_PLATFORMS.length)}
                    </span>
                    &nbsp;/&nbsp;{ALL_SOURCE_PLATFORMS.length}
                  </span>

                  {/* Dot indicators */}
                  <div className="flex items-center gap-1 ml-auto mr-2">
                    {Array.from({ length: totalSourcePages }).map((_, di) => (
                      <button
                        key={di}
                        onClick={() => setSourcePageIndex(di)}
                        className={`h-1.5 rounded-full transition-all ${di === sourcePageIndex ? 'w-4 bg-cyan-400' : 'w-1.5 bg-zinc-600 hover:bg-zinc-400'}`}
                        aria-label={`Go to source page ${di + 1}`}
                      />
                    ))}
                  </div>

                  {/* Next arrow */}
                  <button
                    onClick={() => setSourcePageIndex(prev => Math.min(totalSourcePages - 1, prev + 1))}
                    disabled={sourcePageIndex === totalSourcePages - 1}
                    className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg border border-zinc-700/60 bg-zinc-900/60 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
                    aria-label="Next sources"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Cards — slide in/out on page change */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={sourcePageIndex}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                    className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-3"
                  >
                {ALL_SOURCE_PLATFORMS.slice(sourcePageIndex * SOURCES_PER_PAGE, (sourcePageIndex + 1) * SOURCES_PER_PAGE).map((p, i) => {
                  const brand = PLATFORM_BRAND[p] || { hex: '#71717a', active: true };
                  const hex = brand.hex;
                  const isActive = brand.active;
                  const barSpeeds = [0.65 + i * 0.04, 0.85 + i * 0.03, 0.55 + i * 0.05];
                  return (
                    <motion.div
                      key={p}
                      initial={{ opacity: 0, y: 16, scale: 0.85 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: i * 0.04, type: 'spring', stiffness: 300, damping: 20 }}
                      whileHover={{ y: -5, scale: 1.07, boxShadow: `0 0 0 1px ${hex}40, 0 8px 28px ${hex}30`, transition: { duration: 0.18 } }}
                      className="relative overflow-hidden rounded-xl border border-zinc-800/60 bg-zinc-900/60 backdrop-blur-sm p-3 text-center cursor-default"
                      style={{ boxShadow: `0 0 0 0px ${hex}00` }}
                    >
                      {/* Radial glow bloom */}
                      <motion.div
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{ background: `radial-gradient(circle at 50% 20%, ${hex}22, transparent 65%)` }}
                        animate={{ opacity: isActive ? [0.5, 1, 0.5] : [0.2, 0.3, 0.2] }}
                        transition={{ duration: 2.2 + (i % 4) * 0.4, repeat: Infinity, ease: 'easeInOut' }}
                      />
                      {/* Streaming data ticker along top edge */}
                      {isActive && (
                        <div className="absolute top-0 left-0 right-0 h-0.5 overflow-hidden rounded-t-xl">
                          <motion.div
                            className="absolute top-0 h-full w-1/3 rounded-full"
                            style={{ background: `linear-gradient(to right, transparent, ${hex}cc, transparent)` }}
                            animate={{ x: ['-100%', '300%'] }}
                            transition={{ duration: 1.8 + i * 0.12, repeat: Infinity, ease: 'linear', delay: i * 0.18 }}
                          />
                        </div>
                      )}
                      {/* Icon with float animation */}
                      <motion.div
                        className="text-xl mb-1.5 leading-none select-none"
                        animate={isActive ? { y: [0, -3, 0] } : {}}
                        transition={{ duration: 2.4 + (i % 5) * 0.4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }}
                      >
                        {platformIcon(p)}
                      </motion.div>
                      {/* Platform name */}
                      <p className="text-xs font-bold truncate mb-1.5 tracking-wide uppercase"
                        style={{ color: hex }}>  
                        {platformName(p)}
                      </p>
                      {/* Signal equalizer bars */}
                      <div className="flex items-end justify-center gap-0.5 h-3.5 mb-1.5">
                        {isActive ? [0, 1, 2].map(bi => (
                          <motion.div
                            key={bi}
                            className="w-1 rounded-sm"
                            style={{ background: hex }}
                            animate={{ height: ['4px', `${10 + bi * 3}px`, '4px'] }}
                            transition={{ duration: barSpeeds[bi], repeat: Infinity, ease: 'easeInOut', delay: bi * 0.22 + i * 0.06 }}
                          />
                        )) : [0, 1, 2].map(bi => (
                          <div key={bi} className="w-1 rounded-sm bg-zinc-700" style={{ height: 4 }} />
                        ))}
                      </div>
                      {/* Status with ping indicator */}
                      <div className="flex items-center justify-center gap-1">
                        <span className="relative flex h-1.5 w-1.5">
                          {isActive ? (
                            <>
                              <motion.span
                                className="absolute inline-flex h-full w-full rounded-full"
                                style={{ backgroundColor: hex }}
                                animate={{ scale: [1, 2.2, 1], opacity: [0.7, 0, 0.7] }}
                                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut', delay: i * 0.1 }}
                              />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: hex }} />
                            </>
                          ) : (
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-zinc-600" />
                          )}
                        </span>
                        <span className="text-xs font-semibold" style={{ color: isActive ? hex : '#a1a1aa' }}>
                          {isActive ? 'Active' : 'Pending'}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* OSINT AI Intelligence Grid */}
              <motion.section 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
                className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6"
              >
                <div className="lg:col-span-1 flex flex-col gap-6">
                   <div className="h-[640px]">
                     <OSINTMap />
                   </div>
                   <div className="flex-1 min-h-[300px] shadow-sm">
                     <SimulationSandbox />
                   </div>
                </div>

                <div className="lg:col-span-2 h-full min-h-[624px]">
                   <PropagationGraph />
                </div>
              </motion.section>

              <div className="glass-panel rounded-2xl p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <BellRing className="w-5 h-5 text-rose-400" />
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-rose-500 rounded-full animate-ping" />
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-rose-500 rounded-full" />
                    </div>
                    <h2 className="text-lg font-bold text-white">Live Critical Alerts</h2>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/25">
                      LIVE
                    </span>
                  </div>
                  <span className="text-xs text-zinc-400">Auto-refresh 10s</span>
                </div>

                {alertsLoading ? (
                  <div className="flex flex-col gap-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-20 bg-zinc-800/50 animate-pulse rounded-xl" />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <AnimatePresence>
                      {(alerts.length > 0 ? alerts : MOCK_CRITICAL_ALERTS).map((alert, idx) => {
                        const risk = safeRisk(alert.risk_score ?? alert.riskScore);
                        const sev = (alert.severity || 'CRITICAL').toUpperCase();
                        const sevStyle = sev === 'CRITICAL'
                          ? { bar: 'bg-rose-500', badge: 'bg-rose-500/15 text-rose-400 border-rose-500/30', glow: 'shadow-rose-500/10' }
                          : sev === 'WARNING'
                          ? { bar: 'bg-amber-500', badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30', glow: 'shadow-amber-500/10' }
                          : { bar: 'bg-cyan-500', badge: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30', glow: 'shadow-cyan-500/10' };
                        return (
                          <motion.div
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ delay: idx * 0.04 }}
                            key={alert.id || idx}
                            className={`relative overflow-hidden rounded-xl border border-zinc-700/50 bg-zinc-800/40 hover:bg-zinc-800/70 transition-all duration-200 shadow-lg ${sevStyle.glow}`}
                          >
                            {/* Left severity accent bar */}
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${sevStyle.bar}`} />
                            <div className="pl-4 pr-4 py-3.5 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                  <span className={`text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${sevStyle.badge}`}>
                                    {sev}
                                  </span>
                                  {alert.type && (
                                    <span className="text-xs font-medium text-zinc-400 bg-zinc-700/50 px-2 py-0.5 rounded border border-zinc-600/30">
                                      {alert.type}
                                    </span>
                                  )}
                                  {alert.platform && (
                                    <span className="text-xs text-zinc-400">{alert.platform}</span>
                                  )}
                                </div>
                                <p className="text-sm text-zinc-200 leading-snug line-clamp-2">
                                  {alert.message || alert.type || 'Unidentified threat pattern detected'}
                                </p>
                                <p className="text-xs text-zinc-400 mt-1">{safeTime(alert.timestamp)}</p>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <div className="text-center">
                                  <p className="text-xs text-zinc-400 mb-0.5">RISK</p>
                                  <div className="flex items-center gap-1">
                                    <div className="w-12 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                                      <div className={`h-full ${risk > 0.7 ? 'bg-rose-500' : risk > 0.5 ? 'bg-amber-500' : 'bg-cyan-500'} transition-all`}
                                        style={{ width: `${(risk * 100).toFixed(0)}%` }} />
                                    </div>
                                    <span className={`text-xs font-bold font-mono ${risk > 0.7 ? 'text-rose-400' : risk > 0.5 ? 'text-amber-400' : 'text-cyan-400'}`}>
                                      {(risk * 100).toFixed(0)}%
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => setSelectedAlert(alert)}
                                  className="px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-semibold rounded-lg transition-colors border border-emerald-500/20">
                                  View
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {currentPage === 'alerts' && !accessDenied && (
            <motion.div
              key="alerts"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <AlertManagement monitoringScope={monitoringScope} />
            </motion.div>
          )}

          {currentPage === 'posts' && !accessDenied && (
            <motion.div
              key="posts"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <PostInvestigation monitoringScope={monitoringScope} />
            </motion.div>
          )}

          {currentPage === 'topics' && !accessDenied && (
            <motion.div
              key="topics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <TrendingTopics />
            </motion.div>
          )}

          {currentPage === 'narrative_map' && !accessDenied && (
            <motion.div
              key="narrative_map"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <NarrativeIntelligence />
            </motion.div>
          )}

          {currentPage === 'simulation' && !accessDenied && (
            <motion.div
              key="simulation"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <CampaignSimulation />
            </motion.div>
          )}

          {currentPage === 'analytics' && !accessDenied && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <Analytics monitoringScope={monitoringScope} />
            </motion.div>
          )}

          {currentPage === 'intelligence_center' && !accessDenied && (
            <motion.div
              key="intelligence_center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <IntelligenceCenter monitoringScope={monitoringScope} />
            </motion.div>
          )}

          {currentPage === 'global_monitor' && !accessDenied && (
            <motion.div
              key="global_monitor"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <GlobalMonitoringDashboard monitoringScope={monitoringScope} />
            </motion.div>
          )}

          {currentPage === 'brand_protection' && !accessDenied && (
            <motion.div
              key="brand_protection"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <BrandProtection />
            </motion.div>
          )}

          {currentPage === 'business_intelligence' && !accessDenied && (
            <motion.div
              key="business_intelligence"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <BusinessIntelligence />
            </motion.div>
          )}

          {accessDenied && (
            <motion.div
              key="access_denied"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <div className="flex flex-col items-center justify-center text-center py-24 px-8">
                <div className="w-16 h-16 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mb-6">
                  <Lock className="w-7 h-7 text-rose-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">Access Restricted</h2>
                <p className="text-zinc-400 max-w-md mb-2">
                  Your organization category{userOrgType ? (
                    <> (<strong className="text-zinc-200">{userOrgType.replace(/_/g, ' ').toUpperCase()}</strong>)</>
                  ) : null} is not authorized to access the{' '}
                  <strong className="text-zinc-200">{currentPage.replace(/_/g, ' ').toUpperCase()}</strong> workspace.
                </p>
                <p className="text-zinc-500 text-sm max-w-md mb-6">
                  To request module activation, contact your account administrator or a system superadmin.
                </p>
                <button
                  onClick={() => setCurrentPage('home')}
                  className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold rounded-xl transition-all border border-zinc-700"
                >
                  Return to Dashboard
                </button>
              </div>
            </motion.div>
          )}

          {currentPage === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full"
            >
              <div className="mb-6 md:mb-8">
                <h1 className="text-xl md:text-3xl font-bold text-white mb-2">Identity & Access Management</h1>
                <p className="text-zinc-400">Government-grade profile and authorization system</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* User Profile Card */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="glass-panel p-8 rounded-2xl flex flex-col items-center relative overflow-hidden group border-cyan-500/20"
                >
                  <div className="absolute top-0 w-full h-32 bg-gradient-to-b from-cyan-500/10 to-transparent" />
                  <div className="w-24 h-24 rounded-full bg-zinc-900 border-2 border-cyan-500/50 p-1 mb-4 relative z-10 shadow-[0_0_30px_rgba(6,182,212,0.3)]">
                    <div className="w-full h-full rounded-full bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center">
                      <User className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-white relative z-10">System Admin</h2>
                  <p className="text-cyan-400 text-sm font-mono font-bold mb-6 tracking-widest relative z-10">CLEARANCE: LEVEL 5</p>
                  
                  <div className="w-full space-y-3 mb-8 relative z-10">
                    <div className="flex items-center justify-between text-sm p-3 bg-zinc-800/40 rounded-xl border border-zinc-700/50">
                      <span className="text-zinc-400 flex items-center gap-2"><Shield className="w-4 h-4"/> Role</span>
                      <span className="text-zinc-200 font-medium">Administrator</span>
                    </div>
                    <div className="flex items-center justify-between text-sm p-3 bg-zinc-800/40 rounded-xl border border-zinc-700/50">
                      <span className="text-zinc-400 flex items-center gap-2"><Users className="w-4 h-4"/> Agency</span>
                      <span className="text-zinc-200 font-medium">Cyber Command</span>
                    </div>
                    <div className="flex items-center justify-between text-sm p-3 bg-zinc-800/40 rounded-xl border border-zinc-700/50">
                      <span className="text-zinc-400 flex items-center gap-2"><Clock className="w-4 h-4"/> Member Since</span>
                      <span className="text-zinc-200 font-medium">Oct 2023</span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => { 
                      setIsAuthenticated(false); 
                      clearAuthSession();
                      setCurrentPage('landing'); 
                    }}
                    className="w-full py-3 px-4 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 font-bold rounded-xl transition-colors border border-rose-500/20 flex items-center justify-center gap-2">
                    <LogOut className="w-4 h-4" /> End Secure Session
                  </button>
                </motion.div>

                <div className="lg:col-span-2 space-y-6">
                  
                  {/* Organization Entitlements Panel */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-panel p-6 rounded-2xl"
                  >
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-cyan-400" /> Active Entitlements
                      </h3>
                      <span className="px-3 py-1 bg-cyan-500/10 text-cyan-300 text-xs font-bold rounded-md border border-cyan-500/20 uppercase tracking-wide">
                        {isUserAdmin ? 'SUPERADMIN' : (userOrgType ? userOrgType.replace(/_/g, ' ') : 'UNSCOPED')}
                      </span>
                    </div>
                    <p className="text-zinc-400 text-xs mb-4">
                      {isUserAdmin
                        ? 'Administrator access — all modules are enabled regardless of organization type.'
                        : 'Modules enabled for your organization category. Contact a superadmin to request changes.'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {entitledFeatures.length > 0 ? (
                        entitledFeatures.map((feat) => (
                          <span
                            key={feat}
                            className="px-3 py-1 bg-zinc-800 text-zinc-300 text-xs rounded-full border border-zinc-700 capitalize"
                          >
                            {feat.replace(/_/g, ' ')}
                          </span>
                        ))
                      ) : (
                        <span className="text-zinc-500 text-sm">No modules enabled</span>
                      )}
                    </div>
                  </motion.div>

                  {/* Security Panel */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="glass-panel p-6 rounded-2xl"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Lock className="w-5 h-5 text-emerald-400" /> Security Controls
                      </h3>
                      <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-bold rounded-md border border-emerald-500/20">PROTECTED</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button className="flex items-start gap-4 p-4 rounded-xl bg-zinc-800/40 hover:bg-zinc-800/80 border border-zinc-700/50 transition-all text-left group">
                        <div className="p-2 rounded-lg bg-zinc-700/50 group-hover:bg-cyan-500/20 transition-colors"><Key className="w-5 h-5 text-zinc-300 group-hover:text-cyan-400" /></div>
                        <div>
                          <p className="text-zinc-200 font-bold text-sm mb-1">Update Passphrase</p>
                          <p className="text-zinc-400 text-xs">Last changed 45 days ago</p>
                        </div>
                      </button>
                      <button className="flex items-start gap-4 p-4 rounded-xl bg-zinc-800/40 hover:bg-zinc-800/80 border border-zinc-700/50 transition-all text-left group">
                        <div className="p-2 rounded-lg bg-emerald-500/20"><ShieldCheck className="w-5 h-5 text-emerald-400" /></div>
                        <div>
                          <p className="text-zinc-200 font-bold text-sm mb-1">Two-Factor Auth</p>
                          <p className="text-emerald-500/70 text-xs font-medium">Currently Active</p>
                        </div>
                      </button>
                      <button className="flex items-start gap-4 p-4 rounded-xl bg-zinc-800/40 hover:bg-zinc-800/80 border border-zinc-700/50 transition-all text-left group">
                        <div className="p-2 rounded-lg bg-zinc-700/50 group-hover:bg-cyan-500/20 transition-colors"><Settings className="w-5 h-5 text-zinc-300 group-hover:text-cyan-400" /></div>
                        <div>
                          <p className="text-zinc-200 font-bold text-sm mb-1">API Key Management</p>
                          <p className="text-zinc-400 text-xs">3 keys active</p>
                        </div>
                      </button>
                    </div>
                  </motion.div>

                  {/* Audit Logs */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="glass-panel p-6 rounded-2xl"
                  >
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-cyan-400" /> Recent Audit Logs
                    </h3>
                    
                    <div className="space-y-3">
                      {[
                        { action: 'RESOLVED_CRITICAL_ALERT', resource: 'Alert #88', time: '10 mins ago', status: 'SUCCESS' },
                        { action: 'SYSTEM_CONFIG_UPDATED', resource: 'Collection Rules', time: '2 hours ago', status: 'SUCCESS' },
                        { action: 'FAILED_API_LOGIN', resource: '192.168.1.45', time: '5 hours ago', status: 'WARNING' },
                        { action: 'DOWNLOAD_REPORT', resource: 'Weekly Misinfo Summary', time: '1 day ago', status: 'SUCCESS' },
                      ].map((log, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/20 border border-zinc-800/50 hover:bg-zinc-800/40 transition-colors cursor-default">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${log.status === 'SUCCESS' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                            <div>
                              <p className="text-sm font-bold text-zinc-300 font-mono">{log.action}</p>
                              <p className="text-xs text-zinc-400">Resource: {log.resource}</p>
                            </div>
                          </div>
                          <span className="text-xs font-medium text-zinc-400">{log.time}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {isUserAdmin && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="glass-panel p-6 rounded-2xl"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                        <div>
                          <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-amber-400" /> Inbound Sales Inquiries
                          </h3>
                          <p className="text-zinc-400 text-xs mt-1">Recent demo and pricing requests submitted from the landing page.</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => downloadInquiryExport('csv')}
                            className="flex items-center gap-2 px-3 py-2 bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-400 rounded-lg text-sm transition-colors border border-emerald-700/40"
                          >
                            <Download className="w-4 h-4" /> CSV
                          </button>
                          <button
                            onClick={() => downloadInquiryExport('json')}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-900/40 hover:bg-blue-800/50 text-blue-400 rounded-lg text-sm transition-colors border border-blue-700/40"
                          >
                            <Download className="w-4 h-4" /> JSON
                          </button>
                          <button
                            onClick={() => loadRecentInquiries()}
                            disabled={inquiriesLoading}
                            className="flex items-center gap-2 px-3 py-2 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 rounded-lg text-sm transition-colors border border-zinc-700/40 disabled:opacity-50"
                          >
                            <RefreshCw className={`w-4 h-4 ${inquiriesLoading ? 'animate-spin' : ''}`} /> Refresh
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {(['all', 'new', 'contacted', 'qualified', 'closed'] as const).map((status) => (
                          <button
                            key={status}
                            onClick={() => {
                              setInquiryStatusFilter(status);
                              setInquiryOffset(0);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                              inquiryStatusFilter === status
                                ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30'
                                : 'bg-zinc-800/40 text-zinc-400 border-zinc-700/50 hover:text-white hover:bg-zinc-700/50'
                            }`}
                          >
                            {status === 'all' ? 'All Status' : status.toUpperCase()}
                          </button>
                        ))}
                      </div>

                      <div className="mb-4 flex flex-col md:flex-row md:items-end gap-3">
                        <div>
                          <label className="block text-[11px] text-zinc-500 uppercase tracking-widest mb-1">Start Date</label>
                          <input
                            type="date"
                            value={inquiryStartDate}
                            onChange={(e) => {
                              setInquiryStartDate(e.target.value);
                              setInquiryOffset(0);
                            }}
                            className="px-3 py-2 rounded-lg bg-zinc-900/70 border border-zinc-700/50 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] text-zinc-500 uppercase tracking-widest mb-1">End Date</label>
                          <input
                            type="date"
                            value={inquiryEndDate}
                            onChange={(e) => {
                              setInquiryEndDate(e.target.value);
                              setInquiryOffset(0);
                            }}
                            className="px-3 py-2 rounded-lg bg-zinc-900/70 border border-zinc-700/50 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                          />
                        </div>
                        <button
                          onClick={() => {
                            setInquiryStartDate('');
                            setInquiryEndDate('');
                            setInquiryOffset(0);
                          }}
                          className="px-3 py-2 rounded-lg text-xs font-semibold border bg-zinc-900/60 text-zinc-300 border-zinc-700/50 hover:bg-zinc-800 transition-colors h-[38px]"
                        >
                          Clear Dates
                        </button>
                      </div>

                      {inquiriesError && (
                        <div className="mb-4 rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
                          {inquiriesError}
                        </div>
                      )}

                      {inquiriesLoading ? (
                        <div className="space-y-3">
                          {[1, 2, 3].map((item) => (
                            <div key={item} className="h-16 rounded-xl bg-zinc-800/50 animate-pulse" />
                          ))}
                        </div>
                      ) : recentInquiries.length > 0 ? (
                        <div className="space-y-3">
                          {recentInquiries.map((inquiry) => (
                            <div key={inquiry.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 rounded-xl bg-zinc-800/20 border border-zinc-800/50">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-widest bg-amber-500/10 text-amber-300 border border-amber-500/20">
                                    {inquiry.inquiry_type}
                                  </span>
                                  <span className="text-sm font-semibold text-white">{inquiry.company}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-widest border ${
                                    inquiry.status === 'closed' ? 'bg-zinc-700/30 text-zinc-300 border-zinc-600/40' :
                                    inquiry.status === 'qualified' ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' :
                                    inquiry.status === 'contacted' ? 'bg-blue-500/10 text-blue-300 border-blue-500/20' :
                                    'bg-cyan-500/10 text-cyan-300 border-cyan-500/20'
                                  }`}>
                                    {inquiry.status}
                                  </span>
                                </div>
                                <p className="text-sm text-zinc-300">{inquiry.name} · {inquiry.email}</p>
                                <p className="text-xs text-zinc-500 mt-1">{inquiry.team_type} · {new Date(inquiry.created_at).toLocaleString()}</p>
                                {editingInquiryId === inquiry.id ? (
                                  <div className="mt-3 space-y-2">
                                    <input
                                      value={inquiryDrafts[inquiry.id]?.assignee ?? ''}
                                      onChange={(e) => handleInquiryDraftChange(inquiry.id, 'assignee', e.target.value)}
                                      placeholder="Assign owner"
                                      className="w-full md:w-64 px-3 py-2 rounded-lg bg-zinc-900/70 border border-zinc-700/50 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                                    />
                                    <textarea
                                      value={inquiryDrafts[inquiry.id]?.notes ?? ''}
                                      onChange={(e) => handleInquiryDraftChange(inquiry.id, 'notes', e.target.value)}
                                      placeholder="Add follow-up notes"
                                      rows={3}
                                      className="w-full max-w-xl px-3 py-2 rounded-lg bg-zinc-900/70 border border-zinc-700/50 text-sm text-white focus:outline-none focus:border-cyan-500/50 resize-none"
                                    />
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <button
                                        onClick={() => saveInquiryDetails(inquiry.id)}
                                        disabled={updatingInquiryId === inquiry.id}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold border bg-cyan-500/15 text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/20 disabled:opacity-50"
                                      >
                                        {updatingInquiryId === inquiry.id ? 'Saving…' : 'Save details'}
                                      </button>
                                      <button
                                        onClick={() => setEditingInquiryId(null)}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold border bg-zinc-900/60 text-zinc-300 border-zinc-700/50 hover:bg-zinc-800"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="mt-2 space-y-1">
                                    <p className="text-xs text-zinc-400">
                                      <span className="text-zinc-500">Assignee:</span> {inquiry.assignee || 'Unassigned'}
                                    </p>
                                    {inquiry.notes && (
                                      <p className="text-xs text-zinc-400 max-w-xl leading-relaxed">
                                        <span className="text-zinc-500">Notes:</span> {inquiry.notes}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`px-2 py-1 rounded-lg text-xs font-semibold border ${inquiry.sales_notified ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-amber-500/10 text-amber-300 border-amber-500/20'}`}>
                                  {inquiry.sales_notified ? 'Notified' : 'Stored'}
                                </span>
                                {(['contacted', 'qualified', 'closed'] as const).map((nextStatus) => (
                                  <button
                                    key={nextStatus}
                                    onClick={() => handleInquiryStatusUpdate(inquiry.id, nextStatus)}
                                    disabled={updatingInquiryId === inquiry.id || inquiry.status === nextStatus}
                                    className="px-2.5 py-1 rounded-lg text-xs font-semibold border bg-zinc-900/60 text-zinc-300 border-zinc-700/50 hover:bg-zinc-800 disabled:opacity-50 disabled:hover:bg-zinc-900/60 transition-colors"
                                  >
                                    {updatingInquiryId === inquiry.id && inquiry.status !== nextStatus ? 'Updating…' : nextStatus}
                                  </button>
                                ))}
                                {editingInquiryId !== inquiry.id && (
                                  <button
                                    onClick={() => startInquiryEdit(inquiry)}
                                    className="px-2.5 py-1 rounded-lg text-xs font-semibold border bg-zinc-900/60 text-zinc-300 border-zinc-700/50 hover:bg-zinc-800 transition-colors"
                                  >
                                    Edit notes
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          <div className="flex items-center justify-between pt-2">
                            <button
                              onClick={() => setInquiryOffset((prev) => Math.max(0, prev - INQUIRIES_PAGE_SIZE))}
                              disabled={inquiryOffset === 0 || inquiriesLoading}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold border bg-zinc-900/60 text-zinc-300 border-zinc-700/50 hover:bg-zinc-800 disabled:opacity-50"
                            >
                              Previous
                            </button>
                            <span className="text-xs text-zinc-500">
                              Page {Math.floor(inquiryOffset / INQUIRIES_PAGE_SIZE) + 1}
                            </span>
                            <button
                              onClick={() => setInquiryOffset((prev) => prev + INQUIRIES_PAGE_SIZE)}
                              disabled={!hasMoreInquiries || inquiriesLoading}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold border bg-zinc-900/60 text-zinc-300 border-zinc-700/50 hover:bg-zinc-800 disabled:opacity-50"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-8 text-center">
                          <Briefcase className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                          <p className="text-white font-semibold">No inquiries found</p>
                          <p className="text-zinc-500 text-sm mt-1">Recent landing-page demo and pricing submissions will appear here.</p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

              </div>
            </motion.div>
          )}

        </AnimatePresence>

        {/* ========== MODAL OVERLAYS ========== */}

        {/* TOPIC DETAIL MODAL */}
        <AnimatePresence>
          {selectedTopic && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedTopic(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 40 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-full max-w-3xl max-h-[85vh] bg-zinc-900 border border-zinc-700/50 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="p-6 border-b border-zinc-800 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Hash className="w-6 h-6 text-cyan-400" />
                        <h2 className="text-2xl font-bold text-white">{selectedTopic.keyword}</h2>
                        <span className={`px-2.5 py-1 text-xs font-bold rounded border ${selectedTopic.average_risk_score > 0.7 ? 'bg-rose-500/20 text-rose-400 border-rose-500/20' : 'bg-amber-500/20 text-amber-400 border-amber-500/20'}`}>
                          {selectedTopic.average_risk_score > 0.7 ? 'CRITICAL' : 'ELEVATED'}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400">Topic intelligence report</p>
                    </div>
                    <button onClick={() => setSelectedTopic(null)} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
                      <X className="w-5 h-5 text-zinc-400" />
                    </button>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-4 p-6 border-b border-zinc-800 shrink-0">
                  <div className="text-center p-4 bg-zinc-800/50 rounded-xl">
                    <p className="text-2xl font-bold text-white">{selectedTopic.post_count || 0}</p>
                    <p className="text-xs text-zinc-400 mt-1">Total Signals</p>
                  </div>
                  <div className="text-center p-4 bg-zinc-800/50 rounded-xl">
                    <p className="text-2xl font-bold text-indigo-400">{selectedTopic.mentions_24h || selectedTopic.post_count || 0}</p>
                    <p className="text-xs text-zinc-400 mt-1">24h Velocity</p>
                  </div>
                  <div className="text-center p-4 bg-zinc-800/50 rounded-xl">
                    <p className="text-2xl font-bold text-rose-400">{((selectedTopic.average_risk_score || 0) * 100).toFixed(1)}%</p>
                    <p className="text-xs text-zinc-400 mt-1">Risk Score</p>
                  </div>
                </div>

                {/* Risk Bar */}
                <div className="px-6 pt-4 shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-400 uppercase tracking-wider">Threat Level</span>
                    <span className="text-xs font-mono text-zinc-400">{((selectedTopic.average_risk_score || 0) * 100).toFixed(0)}%</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(selectedTopic.average_risk_score || 0) * 100}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className={`h-full rounded-full ${riskGradient(selectedTopic.average_risk_score || 0)}`}
                    />
                  </div>
                </div>

                {/* Related Posts */}
                <div className="p-6 overflow-y-auto flex-1 min-h-0">
                  <h3 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <FileSearch className="w-4 h-4 text-cyan-400" />
                    Related Posts ({topicRelatedPosts.length})
                  </h3>
                  {topicPostsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full" />
                      <span className="ml-3 text-zinc-400 text-sm">Scanning database...</span>
                    </div>
                  ) : topicRelatedPosts.length === 0 ? (
                    <div className="text-center py-8 text-zinc-400 text-sm">No related posts found in current dataset</div>
                  ) : (
                    <div className="space-y-3">
                      {topicRelatedPosts.map((post, idx) => (
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.03 }}
                          key={idx}
                          className="p-4 bg-zinc-800/40 rounded-xl border border-zinc-700/30 hover:border-zinc-600/50 transition-colors cursor-pointer"
                          onClick={() => { setSelectedTopic(null); setTimeout(() => setSelectedPost(post), 200); }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{platformIcon(post.platform)}</span>
                              <span className="text-xs text-zinc-400 font-medium">@{post.author}</span>
                              <span className="text-xs text-zinc-400">•</span>
                              <span className="text-xs text-zinc-400">{platformName(post.platform)}</span>
                            </div>
                            <span className={`text-xs font-bold font-mono px-2 py-0.5 rounded ${riskColor(post.risk_score || 0)} ${riskBg(post.risk_score || 0)}`}>
                              {(post.risk_score || 0).toFixed(2)}
                            </span>
                          </div>
                          <p className="text-sm text-zinc-300 line-clamp-2">{post.text}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400">
                            <span>👍 {post.likes || 0}</span>
                            <span>💬 {post.comments || 0}</span>
                            <span>↗️ {post.shares || 0}</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* POST DETAIL MODAL */}
        <AnimatePresence>
          {selectedPost && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedPost(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 40 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-full max-w-2xl max-h-[85vh] bg-zinc-900 border border-zinc-700/50 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="p-6 border-b border-zinc-800 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-lg">
                      {platformIcon(selectedPost.platform)}
                    </div>
                    <div>
                      <p className="font-bold text-white">@{selectedPost.author}</p>
                      <p className="text-xs text-zinc-400">{platformName(selectedPost.platform)}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedPost(null)} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
                
                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1 min-h-0">
                  <p className="text-zinc-200 leading-relaxed mb-6 text-base">{selectedPost.text}</p>
                  
                  {/* Risk Score Bar */}
                  <div className="mb-6 p-4 bg-zinc-800/30 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-zinc-400 uppercase tracking-wider font-bold">Risk Assessment</span>
                      <span className={`text-lg font-bold font-mono ${riskColor(selectedPost.risk_score || 0)}`}>
                        {(selectedPost.risk_score || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }} 
                        animate={{ width: `${(selectedPost.risk_score || 0) * 100}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className={`h-full rounded-full ${riskGradient(selectedPost.risk_score || 0)}`} 
                      />
                    </div>
                  </div>
                  
                  {/* Engagement Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-4 bg-zinc-800/40 rounded-xl border border-zinc-700/30">
                      <p className="text-2xl font-bold text-white">{selectedPost.likes || 0}</p>
                      <p className="text-xs text-zinc-400 mt-1">Likes</p>
                    </div>
                    <div className="text-center p-4 bg-zinc-800/40 rounded-xl border border-zinc-700/30">
                      <p className="text-2xl font-bold text-white">{selectedPost.comments || 0}</p>
                      <p className="text-xs text-zinc-400 mt-1">Comments</p>
                    </div>
                    <div className="text-center p-4 bg-zinc-800/40 rounded-xl border border-zinc-700/30">
                      <p className="text-2xl font-bold text-white">{selectedPost.shares || 0}</p>
                      <p className="text-xs text-zinc-400 mt-1">Shares</p>
                    </div>
                  </div>
                  
                  {/* Metadata */}
                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between p-3 bg-zinc-800/30 rounded-lg">
                      <span className="text-zinc-400 text-sm">Platform</span>
                      <span className="text-zinc-200 text-sm font-medium">{platformName(selectedPost.platform)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-zinc-800/30 rounded-lg">
                      <span className="text-zinc-400 text-sm">Sentiment Score</span>
                      <span className={`text-sm font-bold font-mono ${(selectedPost.sentiment_score || 0) > 0 ? 'text-emerald-400' : (selectedPost.sentiment_score || 0) < 0 ? 'text-rose-400' : 'text-zinc-300'}`}>
                        {(selectedPost.sentiment_score || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-zinc-800/30 rounded-lg">
                      <span className="text-zinc-400 text-sm">Collected</span>
                      <span className="text-zinc-200 text-sm">{selectedPost.created_at ? new Date(selectedPost.created_at).toLocaleString() : 'N/A'}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {selectedPost.url && (
                    <a 
                      href={selectedPost.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center justify-center gap-2 w-full p-3 bg-cyan-500/10 text-cyan-400 rounded-xl hover:bg-cyan-500/20 transition-colors border border-cyan-500/20 font-medium text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Original Source
                    </a>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ALERT DETAIL MODAL */}
        <AnimatePresence>
          {selectedAlert && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedAlert(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 40 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-full max-w-lg bg-zinc-900 border border-zinc-700/50 rounded-2xl overflow-hidden shadow-2xl shadow-black/50"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Severity Header */}
                <div className={`p-6 border-b border-zinc-800 ${
                  selectedAlert.severity === 'CRITICAL' ? 'bg-gradient-to-r from-rose-500/10 to-red-500/10' :
                  selectedAlert.severity === 'HIGH' ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10' :
                  'bg-gradient-to-r from-cyan-500/10 to-blue-500/10'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`px-3 py-1.5 text-xs font-bold uppercase rounded-lg border ${
                        selectedAlert.severity === 'CRITICAL' ? 'bg-rose-500/20 text-rose-400 border-rose-500/20' :
                        selectedAlert.severity === 'HIGH' ? 'bg-amber-500/20 text-amber-400 border-amber-500/20' :
                        selectedAlert.severity === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/20' :
                        'bg-blue-500/20 text-blue-400 border-blue-500/20'
                      }`}>
                        {selectedAlert.severity}
                      </span>
                      <h2 className="text-lg font-bold text-white mt-3 leading-snug">{selectedAlert.message || selectedAlert.type || 'Alert Details'}</h2>
                    </div>
                    <button onClick={() => setSelectedAlert(null)} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors self-start">
                      <X className="w-5 h-5 text-zinc-400" />
                    </button>
                  </div>
                </div>
                
                {/* Details */}
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-zinc-800/50 rounded-xl text-center">
                      <p className={`text-2xl font-bold font-mono ${riskColor(selectedAlert.risk_score || 0)}`}>{(selectedAlert.risk_score || 0).toFixed(2)}</p>
                      <p className="text-xs text-zinc-400 mt-1">Risk Score</p>
                    </div>
                    <div className="p-4 bg-zinc-800/50 rounded-xl text-center">
                      <p className={`text-xl font-bold ${selectedAlert.status === 'resolved' ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {(selectedAlert.status || 'OPEN').toUpperCase()}
                      </p>
                      <p className="text-xs text-zinc-400 mt-1">Status</p>
                    </div>
                  </div>
                  
                  {/* Risk Bar */}
                  <div className="mb-6">
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(selectedAlert.risk_score || 0) * 100}%` }}
                        transition={{ duration: 0.8 }}
                        className={`h-full rounded-full ${riskGradient(selectedAlert.risk_score || 0)}`}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between p-3 bg-zinc-800/30 rounded-lg">
                      <span className="text-zinc-400 text-sm">Alert ID</span>
                      <span className="text-zinc-200 font-mono text-sm">{selectedAlert.id}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-zinc-800/30 rounded-lg">
                      <span className="text-zinc-400 text-sm">Detected</span>
                      <span className="text-zinc-200 text-sm">{selectedAlert.timestamp ? new Date(selectedAlert.timestamp).toLocaleString() : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-zinc-800/30 rounded-lg">
                      <span className="text-zinc-400 text-sm">Category</span>
                      <span className="text-zinc-200 text-sm">{selectedAlert.type || selectedAlert.severity}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button className="flex-1 py-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 font-bold rounded-xl transition-colors border border-emerald-500/20 text-sm">
                      Mark Resolved
                    </button>
                    <button onClick={() => setSelectedAlert(null)} className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold rounded-xl transition-colors border border-zinc-700 text-sm">
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>


        <AnimatePresence>
          {showProfileSetup && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[700] flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 16 }}
                className="w-full max-w-lg bg-zinc-900 border border-zinc-700/60 rounded-2xl p-6"
              >
                <h3 className="text-xl font-bold text-white mb-1">Complete your profile</h3>
                <p className="text-sm text-zinc-400 mb-4">Set your location to personalize monitoring scope and enable regional filtering.</p>
                <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Location</label>
                <input
                  type="text"
                  value={profileLocationInput}
                  onChange={(e) => setProfileLocationInput(e.target.value)}
                  placeholder="e.g. India, New Delhi"
                  className="w-full bg-zinc-800/70 border border-zinc-700/60 rounded-xl px-4 py-2.5 text-zinc-200 focus:outline-none focus:border-cyan-500/60"
                />
                {profileSetupError && (
                  <p className="text-xs text-rose-400 mt-2">{profileSetupError}</p>
                )}
                <div className="mt-5 flex justify-end">
                  <button
                    onClick={handleProfileSetupSave}
                    disabled={profileSetupSaving}
                    className="px-4 py-2 rounded-xl bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 font-semibold text-sm hover:bg-cyan-500/30 disabled:opacity-50"
                  >
                    {profileSetupSaving ? 'Saving…' : 'Save and Continue'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
